import { UpChunk, createUpload } from '@mux/upchunk';
import axios from 'axios';
import auth from '@client/components/auth';
import _ from 'lodash';
import { UploadMap, UploadState } from './library.domain';
import { mixTrack } from '@services/mixpanel';
import { loadVideo } from '@client/new/helpers/load-video-from-file';
import { durationToLabel } from '../helpers';
import { getFileNameWihoutExtension } from '../helpers';

export type UseVideosParams = {
  influencerId: number;
};

export type UploadVideoParams = {
  video: UploadFile;
  influencerId: number;
  setUploadMap: React.Dispatch<React.SetStateAction<UploadMap>>;
  uploadMap: UploadMap;
  onSuccess?: (id: string) => void;
  folderId: number | null;
};

export type MuxVideoParams = {
  chosenThumbnail?: string;
};

export type MuxUploadParams = {
  video: UploadFile;
  influencerId: number;
  editParams?: MuxVideoParams;
  onProgress: (progress: number) => void;
  onSuccess?: (id: string) => void;
  setMuxInstance: (instance: UpChunk) => void;
  folderId: number | null;
};

export type UploadFile = {
  file: File;
  thumbnailTimestamp?: number;
  id: string;
};

export type BulkUploadParams = {
  uploadMap: UploadMap;
  setUploadMap: React.Dispatch<React.SetStateAction<UploadMap>>;
  influencerId: number;
  videos: UploadFile[];
  onSuccess?: () => void;
  folderId: number | null;
};

export const bulkUpload = async ({
  videos,
  influencerId,
  setUploadMap,
  uploadMap,
  onSuccess,
  folderId,
}: BulkUploadParams): Promise<void> => {
  const batches = _.chunk(videos, import.meta.env.VITE_MUX_CONCURRENT_UPLOADS);

  for (const videosBatch of batches) {
    const uploadPromises = videosBatch.map((v) =>
      uploadFile({
        video: v,
        influencerId,
        setUploadMap,
        uploadMap,
        onSuccess,
        folderId,
      }),
    );
    await Promise.all(uploadPromises);
  }
};

export const uploadFile = async ({
  video,
  setUploadMap,
  influencerId,
  onSuccess,
  folderId,
}: UploadVideoParams) => {
  const updateState = (delta: Partial<UploadState>) => {
    setUploadMap((prev) => ({
      ...prev,
      [video.id]: { ...prev[video.id], ...delta },
    }));
  };

  updateState({
    fileId: video.id,
    fileName: video.file.name,
    progress: 0,
    state: 'uploading',
    folderId,
  });

  try {
    await muxUpload({
      video: video,
      influencerId: influencerId,
      folderId,
      onProgress: (value: number) =>
        updateState({
          progress: value,
          state: value === 100 ? 'finished' : 'uploading',
        }),
      onSuccess,
      editParams: {
        ...(video.thumbnailTimestamp
          ? { chosenThumbnail: String(video.thumbnailTimestamp) }
          : {}),
      },
      setMuxInstance: (value) =>
        setUploadMap((prev) => ({
          ...prev,
          [video.id]: { ...prev[video.id], muxInstance: value },
        })),
    });

    loadVideo(video.file).then((video) =>
      mixTrack({
        type: 'Video Uploaded',
        payload: {
          'Files Uploaded': '1',
          'Upload Location': 'Library',
          'Video Type': durationToLabel(video.duration),
        },
      }),
    );
  } catch (error) {
    console.error('Upload error', error);
    return updateState({
      state: 'error',
    });
  }
};

const chunkSize = 5120;

const muxUpload = async ({
  influencerId,
  onProgress,
  video,
  editParams,
  onSuccess,
  setMuxInstance,
  folderId,
}: MuxUploadParams) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (res, rej) => {
    try {
      const data = await generateMuxSignedUrl({
        influencerId,
        fileSize: video.file.size,
        title: getFileNameWihoutExtension(video.file.name),
        folderId,
        ...editParams,
      });

      if (data.signedURL) {
        const muxUploadInstance = await createUpload({
          file: video.file,
          endpoint: data.signedURL,
          chunkSize,
        });
        setMuxInstance(muxUploadInstance);

        muxUploadInstance.on('error', (e) => rej(e.detail));
        muxUploadInstance.on('progress', (p) => onProgress(p.detail));
        muxUploadInstance.on('success', (p) => {
          onSuccess && onSuccess(data.videoId);
          res(p);
        });
      }
    } catch (e) {
      rej(e);
    }
  });

const generateMuxSignedUrl = ({
  influencerId,
  chosenThumbnail,
  fileSize,
  title,
  folderId,
}: {
  influencerId: number;
  fileSize: number;
  title: string;
  chosenThumbnail?: string;
  folderId: number | null;
}): Promise<{
  signedURL: string;
  videoId: string;
}> =>
  axios
    .post(
      `${import.meta.env.VITE_MUX_API_URL}/signed-url`,
      {
        influencerId,
        chosenThumbnail,
        fileSize,
        title,
        folderId,
      },
      {
        headers: {
          Authorization: auth.getToken(),
        },
      },
    )
    .then((response) => {
      if (response.status === 401) {
        throw new Error('auth');
      }
      return response.data;
    })
    .then((result) => ({
      signedURL: result.signedURL,
      videoId: result.video.id,
    }));
